import { cn } from "@/utils/ui";

import { Checkbox } from "@components/ui/checkbox";
import { Input } from "@components/ui/input";

/**
 * Speaker roles the examiner can mark as present in the session. There is no
 * backend contract for the individual roles (MDRS-115). Doctor/Examinee
 * default checked (the standard two-party assessment); Interpreter/Other are
 * opt-in. The checked roles drive both the diarization speaker count (the
 * caller derives `maxSpeakers` from how many are checked) AND the display
 * names live diarization assigns speakers by first-appearance order — see
 * AudioRecorder's `checkedRoleOrder` / extendSpeakerRoles's `roleOrder` param.
 */
export const SPEAKER_ROLES = ["Doctor", "Examinee", "Interpreter", "Other"] as const;
export type SpeakerRole = (typeof SPEAKER_ROLES)[number];
export const DEFAULT_SPEAKERS: Record<SpeakerRole, boolean> = {
  Doctor: true,
  Examinee: true,
  Interpreter: false,
  Other: false,
};

/**
 * A checked speaker's role + real display name, in checklist order (MDRS-163
 * follow-on). Forwarded end-to-end from AudioRecorder through session-end into
 * the batch summarize payload as `speaker_hints`, so the batch diarization
 * pipeline can identify speakers by the same real names shown in this modal
 * instead of generic role words — see AudioRecorder's `speakerHints`.
 */
export type SpeakerHint = { role: SpeakerRole; name: string };

type SpeakerChecklistProps = {
  /** Which roles are currently checked. */
  speakers: Record<SpeakerRole, boolean>;
  /** Display name per role (e.g. real examiner/examinee names), editable. */
  labels: Record<SpeakerRole, string>;
  onToggle: (role: SpeakerRole, checked: boolean) => void;
  /**
   * Called as the user types a speaker's name (e.g. correcting a typo, or
   * naming the Interpreter/Other role instead of leaving the generic role
   * word). Fed straight into `roleOrder` — see AudioRecorder.tsx.
   */
  onNameChange: (role: SpeakerRole, name: string) => void;
  /**
   * True while the session is connecting/recording — the whole list is greyed
   * and non-interactive because `max_speakers` is locked once connected.
   */
  sessionLocked?: boolean;
};

export const SpeakerChecklist = ({
  speakers,
  labels,
  onToggle,
  onNameChange,
  sessionLocked = false,
}: SpeakerChecklistProps) => (
  <div className="space-y-2">
    <p className="text-sm font-semibold text-background-dark">
      Select speakers present in the session
    </p>
    <div className="space-y-1">
      {SPEAKER_ROLES.map((role) => {
        // The checkbox and the name field are two independent controls now
        // (the name field replaced the old role-word <label>), so each gets
        // its own id/accessible name instead of sharing one via htmlFor.
        const checkboxId = `speaker-${role.toLowerCase()}`;
        const nameId = `speaker-${role.toLowerCase()}-name`;
        // Doctor is the examiner — always present, so the row is locked checked
        // and can't be unselected (MDRS-115). Locking doesn't dim the row (it's
        // a permanent selection, not a disabled option); only the session lock
        // greys the whole list. The name field stays editable even when the
        // checkbox itself is locked — the lock is about presence, not identity.
        const locked = role === "Doctor";
        const checkboxDisabled = locked || sessionLocked;
        const nameDisabled = !speakers[role] || sessionLocked;
        return (
          <div
            key={role}
            className={cn("flex items-center gap-2", sessionLocked && "opacity-60")}
          >
            <Checkbox
              id={checkboxId}
              aria-label={role}
              checked={speakers[role]}
              disabled={checkboxDisabled}
              onCheckedChange={(checked) => onToggle(role, checked === true)}
            />
            <Input
              id={nameId}
              aria-label={`Name for ${role}`}
              value={labels[role]}
              disabled={nameDisabled}
              onChange={(e) => onNameChange(role, e.target.value)}
              className="h-8 max-w-64 text-sm"
            />
          </div>
        );
      })}
    </div>
  </div>
);
